/**
 * `fetchApi` (lib/api.ts) never throws on a refused or failed request: it ANSWERS `{ success: false, error }`
 * (a 403, a 400, a timeout, a dropped connection). react-query only runs `onError` when the mutation function
 * throws, so a `mutationFn` that just returns the answer makes a refused save look like a success: the success
 * toast shows, the form closes, and the next refetch quietly undoes the change.
 *
 * Every `mutationFn` that calls `api.*` runs the answer through `unwrapApi`, so a refusal becomes a real error:
 * `onError` runs (rollback, visible message) and `onSuccess` does not.
 *
 *   mutationFn: (id: number) => api.qaReports.delete(id).then(unwrapApi)
 *
 * The success path is unchanged: the answer is returned as is, so `onSuccess` keeps reading `res.data`.
 * `lib/mutation-fn-surfaces-failure.test.ts` fails the build when a new `mutationFn` skips this.
 */

/** The part of `ApiResponse` the helper needs; `ApiResponse<T>` and the looser contact answers both fit. */
interface ApiAnswerLike {
  success: boolean;
  error?: { code?: string; message?: string };
}

/** Thrown for a refused or failed API answer. Carries the server's code so a screen can tell refusals apart. */
export class ApiRequestError extends Error {
  readonly code: string | undefined;

  constructor(message: string, code?: string) {
    super(message);
    this.name = 'ApiRequestError';
    this.code = code;
  }
}

/** Return the answer when it succeeded; throw `ApiRequestError` (with the server's message) when it did not. */
export function unwrapApi<R extends ApiAnswerLike>(answer: R): R {
  if (!answer.success) {
    throw new ApiRequestError(answer.error?.message || 'Request failed', answer.error?.code);
  }
  return answer;
}

/** A message safe to show a person for whatever a failed mutation threw. */
export function failureMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}
